import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MediaObserver } from '@angular/flex-layout';

import { MingaPermission } from 'libs/domain';
import { get } from 'lodash';
import { Observable, of, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

import { PermissionsService } from '@app/src/app/permissions';

import { AppRuntime, AppRuntimeInterface } from '@shared/services/app-runtime';
import { MediaService } from '@shared/services/media';
import { xlsxExport } from '@shared/utils/xlsx-export';

import { MmDashboardTableComponent } from './components/mm-dashboard-table/mm-dashboard-table.component';
import {
  MM_DASHBOARD_COLUMN_PRESET_OPTIONS,
  MmDashboardColumnPresets,
  MmDashboardFilter,
  MmDashboardMessages,
  MmDashboardTableColumn,
  MmDashboardTableColumnToMessage,
  TOGGLE_COLUMNS_OPTIONS,
} from './constants';
import { MmDashboardFiltersService, MmDashboardService, MmDashboardTableService } from './services';
import { ToggleMenuData } from './types';

@Component({
  selector: 'mg-ma-dashboard',
  templateUrl: './mm-dashboard.component.html',
  styleUrls: ['./mm-dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MmDashboardComponent implements OnInit, OnDestroy {
  // Children

  @ViewChild(MmDashboardTableComponent)
  dashTable: MmDashboardTableComponent;

  // Constants

  public readonly MESSAGES = MmDashboardMessages;
  public readonly FILTERS = MmDashboardFilter;
  public readonly PRESETS = MM_DASHBOARD_COLUMN_PRESET_OPTIONS;
  public readonly PRESET_TYPES = MmDashboardColumnPresets;

  // Observables
  private readonly _destroyedSubject = new ReplaySubject<void>(1);
  public readonly media$: Observable<any>;
  public readonly toggleOptions$: Observable<ToggleMenuData>;
  public readonly isSuperAdmin$ = this._permissions.observePermission(MingaPermission.SUPERADMIN);

  // Getters

  get canDownloadExport(): boolean {
    return !this._runtime.isNativeApp();
  }

  // Constructor

  constructor(
    public media: MediaService,
    public mediaObserver: MediaObserver,
    public dashboardFilters: MmDashboardFiltersService,
    public dashboardService: MmDashboardService,
    public dashboardTable: MmDashboardTableService,
    private _permissions: PermissionsService,
    @Inject(AppRuntimeInterface) private _runtime: AppRuntime,
  ) {
    this.media$ = this.mediaObserver.asObservable().pipe(
      takeUntil(this._destroyedSubject),
      map(change => change[0].mqAlias),
      distinctUntilChanged(),
    );

    let toggleOptions: ToggleMenuData = [];
    if (!this._permissions.hasPermission(MingaPermission.SUPERADMIN)) {
      TOGGLE_COLUMNS_OPTIONS.forEach(option => {
        if (!option.sa) {
          option.options = option.options.filter(column => !column.sa);
          toggleOptions.push(option);
        }
      });
    } else {
      toggleOptions = TOGGLE_COLUMNS_OPTIONS;
    }
    this.toggleOptions$ = of(toggleOptions);
  }

  // Lifecycle Hooks

  ngOnInit(): void {
    this.dashboardService.fetchAllData();
  }

  ngOnDestroy(): void {
    this._destroyedSubject.next();
    this._destroyedSubject.complete();
  }

  // Public Methods

  public getfilteredData() {
    if (this.dashTable) {
      return this.dashTable.getFilteredData();
    }
    return [];
  }

  public getColumns() {
    if (this.dashTable) {
      return this.dashTable.getColumns();
    }
    return [];
  }

  public export() {
    const filterData = this.getfilteredData();
    const columns = this.getColumns();

    const data = filterData.map((minga: any) =>
      columns.map(column => {
        if (column === MmDashboardTableColumn.SIS) {
          return minga.sisType || minga.rostering || 'N/A';
        }
        return get(minga, column);
      }),
    );
    const headers = columns.map(column => MmDashboardTableColumnToMessage[column]);

    xlsxExport(headers, data, 'mingas.xlsx');
  }
}
